import React from 'react'
import { arrayOf, number, shape, string, oneOf, object, bool } from 'prop-types'
import styles from './EventUmbrellaCard.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Stack from '@/component/Primitive/Stack'
import Hide from '@/component/Primitive/Hide'
import SmartLink from '@/component/Primitive/SmartLink'
import Type from '@/component/Primitive/Type'
import { PlayButton, Highlight } from '@/component/Primitive/Events'

const EventUmbrellaCard = ({
  type,
  title,
  subtitle,
  image,
  theme,
  ctaLink,
  inverse,
  logo,
  fullUrlPath,
  align,
  openInNewTab
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image || {}
  const { mobile: mobileLogo, desktop: deskotpLogo } = logo || {}

  const Component = (
    <div
      className={cx(
        styles.EventUmbrellaCardInner,
        inverse && styles.inverse,
        styles[type]
      )}
    >
      <div className={styles.Content}>
        <Stack gap="small">
          <div className={styles.ImagesWrapper}>
            {desktopImage && (
              <Image
                device="desktop"
                image={desktopImage}
                logo={deskotpLogo}
                options={{ showPlayButton: type === 'video', type }}
              />
            )}
            {mobileImage && (
              <Image
                device="mobile"
                image={mobileImage}
                logo={mobileLogo}
                options={{ showPlayButton: type === 'video', type }}
              />
            )}
          </div>
          <Stack gap="tiny">
            {title && (
              <Type
                as="h1"
                size={['body4', 'body3']}
                weight="medium"
                className={styles.Title}
              >
                <Highlight>{title}</Highlight>
              </Type>
            )}
            {subtitle && (
              <Type
                as="h2"
                size="body3"
                weight="regular"
                className={styles.Subtitle}
              >
                {subtitle}
              </Type>
            )}
          </Stack>
          <SmartLink to="#" className={styles.Cta}>
            <Type
              as="h2"
              size="body3"
              weight="medium"
              className={styles.CtaLabel}
            >
              View all events
            </Type>
          </SmartLink>
        </Stack>
      </div>
    </div>
  )
  const getUrl = () => {
    if (!fullUrlPath) return null
    if (fullUrlPath.includes('http:') || fullUrlPath.includes('https:'))
      return fullUrlPath
    return `/${fullUrlPath}`
  }

  return (
    <div
      className={cx(
        styles.EventUmbrellaCard,
        theme === 'dark' && styles.dark,
        align === 'center' && styles.center
      )}
    >
      {ctaLink || getUrl() ? (
        <SmartLink
          href={ctaLink || getUrl()}
          className={styles.EventUmbrellaCardOverlayLink}
          {...(openInNewTab && { target: '_blank' })}
        >
          {Component}
        </SmartLink>
      ) : (
        Component
      )}
    </div>
  )
}

EventUmbrellaCard.propTypes = {
  type: string,
  title: string,
  subtitle: string,
  theme: string,
  ctaLink: string,
  fullUrlPath: string,
  inverse: bool,
  align: string,
  openInNewTab: bool,
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  }),
  logo: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, logo, options = {} }) => {
  const { showPlayButton, type } = options
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const className =
    device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide
      {...hideProps}
      className={cx(styles.ImageWrapper, type === 'speaker' && styles.speaker)}
    >
      <ResponsiveImage className={cx(styles.Image, className)} {...image} />
      {logo && (
        <ResponsiveImage className={cx(styles.Logo, className)} {...logo} />
      )}
      {showPlayButton && (
        <PlayButton
          className={styles.Play}
          options={{
            size: [60, 60],
            disableClick: true
          }}
        />
      )}
    </Hide>
  )
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  logo: object,
  options: object
}

export default EventUmbrellaCard
